import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/axios';
import { KeyRound, Loader2, CheckCircle2 } from 'lucide-react';
import logo from '../assets/styles/logo.png';
import bgImage from '../assets/styles/variables/1233456.jpg';
import { useLang } from '../contexts/LanguageContext';

export default function ResetPassword() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError(lang === 'tr' ? 'Şifre en az 6 karakter olmalıdır' : 'Passwort muss mindestens 6 Zeichen haben'); return; }
    if (password !== confirm) { setError(lang === 'tr' ? 'Şifreler eşleşmiyor' : 'Passwörter stimmen nicht überein'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.error || (lang === 'tr' ? 'Sıfırlama sırasında hata oluştu' : 'Fehler beim Zurücksetzen'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <img src={logo} alt="Galatasaray Gelsenkirchen e.V." className="mx-auto w-24 h-24 object-contain mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">{lang === 'tr' ? 'Şifreyi Sıfırla' : 'Passwort zurücksetzen'}</h1>
            <p className="text-gray-500 mt-1 text-sm">{lang === 'tr' ? 'Yeni şifre belirleyin' : 'Neues Passwort festlegen'}</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
          )}

          {!token ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600">{lang === 'tr' ? 'Geçersiz bağlantı. Lütfen sıfırlamayı yeniden talep edin.' : 'Ungültiger Link. Bitte fordern Sie das Zurücksetzen erneut an.'}</p>
              <Link to="/login" className="text-sm text-gs-red font-medium hover:underline">{lang === 'tr' ? 'Giriş Sayfasına Dön' : 'Zur Anmeldung'}</Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-3">
              <CheckCircle2 className="mx-auto text-green-500" size={44} />
              <p className="text-sm text-gray-600">{lang === 'tr' ? 'Şifreniz başarıyla değiştirildi. Giriş sayfasına yönlendiriliyorsunuz…' : 'Ihr Passwort wurde erfolgreich geändert. Sie werden zur Anmeldung weitergeleitet…'}</p>
              <Link to="/login" className="text-sm text-gs-red font-medium hover:underline">{lang === 'tr' ? 'Şimdi Giriş Yap' : 'Jetzt anmelden'}</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === 'tr' ? 'Yeni Şifre' : 'Neues Passwort'}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-yellow focus:border-gs-yellow outline-none transition-colors" placeholder={lang === 'tr' ? 'En az 6 karakter' : 'Mindestens 6 Zeichen'} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{lang === 'tr' ? 'Şifreyi Onayla' : 'Passwort bestätigen'}</label>
                <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-yellow focus:border-gs-yellow outline-none transition-colors" placeholder={lang === 'tr' ? 'Şifreyi tekrar girin' : 'Passwort wiederholen'} />
              </div>
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gs-red text-white py-3 min-h-[48px] rounded-lg font-medium hover:bg-gs-red/90 transition-colors disabled:opacity-50">
                {loading ? <Loader2 size={20} className="animate-spin" /> : <KeyRound size={20} />}
                {loading ? (lang === 'tr' ? 'Kaydediliyor...' : 'Wird gespeichert...') : (lang === 'tr' ? 'Şifreyi Değiştir' : 'Passwort ändern')}
              </button>
              <Link to="/login" className="block text-center text-sm text-gray-500 hover:text-gray-700">{lang === 'tr' ? 'Giriş Sayfasına Dön' : 'Zurück zur Anmeldung'}</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
